import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import apiService from '../services/api';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminSourcesPage = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    domain: '',
    category: 'General',
    credibility: 'Medium',
    tier: 2,
    tier_description: '',
    region: '',
    description: ''
  });

  useEffect(() => {
    checkAdminAccess();
    loadSources();
  }, []);

  const checkAdminAccess = () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && user.is_admin) {
      setIsAdmin(true);
    } else {
      setError('Admin access required');
    }
  };

  const loadSources = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/sources/`);
      setSources(response.data);
    } catch (err) {
      setError('Failed to load sources');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      domain: '',
      category: 'General',
      credibility: 'Medium',
      tier: 2,
      tier_description: '',
      region: '',
      description: ''
    });
    setEditingSource(null);
  };

  const handleAddSource = async () => {
    if (!isAdmin) {
      setError('Admin access required');
      return;
    }

    setError('');
    try {
      const response = await axios.post(`${API}/sources/`, formData, {
        withCredentials: true
      });
      setSources([...sources, response.data]);
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      // Handle validation errors from Pydantic
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation error - extract message
        const messages = detail.map(e => e.msg || 'Validation error').join('. ');
        setError(messages);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to add source');
      }
    }
  };

  const handleEditSource = (source) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      url: source.url,
      domain: source.domain,
      category: source.category,
      credibility: source.credibility,
      tier: source.tier || 2,
      tier_description: source.tier_description || '',
      region: source.region || '',
      description: source.description || ''
    });
    setShowAddModal(true);
  };

  const handleUpdateSource = async () => {
    if (!isAdmin || !editingSource) return;

    setError('');
    try {
      const response = await axios.put(
        `${API}/sources/${editingSource.source_id}`,
        formData,
        { withCredentials: true }
      );
      setSources(sources.map(s => s.source_id === editingSource.source_id ? response.data : s));
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      // Handle validation errors from Pydantic
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(e => e.msg || 'Validation error').join('. ');
        setError(messages);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to update source');
      }
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to deactivate this source?')) return;

    try {
      await axios.delete(`${API}/sources/${sourceId}`, {
        withCredentials: true
      });
      setSources(sources.filter(s => s.source_id !== sourceId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete source');
    }
  };

  const handleSeedDefaults = async () => {
    if (!isAdmin) return;
    if (!window.confirm('This will add default trusted sources. Continue?')) return;

    try {
      const response = await axios.post(`${API}/sources/seed`, {}, {
        withCredentials: true
      });
      alert(`Added ${response.data.added} sources, skipped ${response.data.skipped} existing`);
      loadSources();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to seed sources');
    }
  };

  const handleSeedTiered = async () => {
    if (!isAdmin) return;
    if (!window.confirm('This will add/update comprehensive tiered sources (Tier 1, 2, 3). Continue?')) return;

    try {
      const response = await axios.post(`${API}/sources/seed-tiered`, {}, {
        withCredentials: true
      });
      alert(`Added: ${response.data.added}, Updated: ${response.data.updated}, Skipped: ${response.data.skipped}`);
      loadSources();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to seed tiered sources');
    }
  };

  const getCredibilityColor = (credibility) => {
    switch (credibility) {
      case 'Highest': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'High': return 'bg-green-100 text-green-800 border-green-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Low': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 1: return { label: 'Tier 1', className: 'bg-purple-600 text-white' };
      case 2: return { label: 'Tier 2', className: 'bg-blue-600 text-white' };
      case 3: return { label: 'Tier 3', className: 'bg-orange-500 text-white' };
      default: return { label: `Tier ${tier}`, className: 'bg-gray-500 text-white' };
    }
  };

  if (!isAdmin && !isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>
            Admin access required. Please contact an administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size="md" data-testid="brand-logo-header" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt - Admin
            </span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">Trusted Sources Management</h1>
              <p className="text-slate-600 dark:text-slate-400">Manage the list of trusted sources for verification</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleSeedTiered}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Seed Tiered Sources
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setError('');
                  setShowAddModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Source
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Sources Grid */}
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sources.map((source) => (
                <Card key={source.source_id} className="hover:shadow-lg transition-shadow dark:bg-slate-800">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg dark:text-slate-100">{source.name}</CardTitle>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditSource(source)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteSource(source.source_id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                        >
                          {source.domain}
                        </a>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className={getTierBadge(source.tier || 2).className}>
                          {getTierBadge(source.tier || 2).label}
                        </Badge>
                        <Badge variant="outline">{source.category}</Badge>
                        <Badge className={getCredibilityColor(source.credibility)}>
                          {source.credibility}
                        </Badge>
                        {source.is_active && (
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">Active</Badge>
                        )}
                      </div>
                      {source.region && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">Region: {source.region}</p>
                      )}
                      {source.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{source.description}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {sources.length === 0 && !isLoading && (
            <div className="text-center py-20">
              <p className="text-slate-600 dark:text-slate-400 mb-4">No sources added yet</p>
              <Button onClick={handleSeedTiered} className="bg-blue-600 hover:bg-blue-700">
                Seed Tiered Sources
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSource ? 'Edit Source' : 'Add Trusted Source'}</DialogTitle>
            <DialogDescription>
              {editingSource ? 'Update the source information' : 'Add a new trusted source for verification'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Source Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., World Health Organization"
              />
            </div>

            <div>
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                type="url"
                value={formData.url}
                onChange={(e) => handleInputChange('url', e.target.value)}
                placeholder="https://example.com"
              />
              <p className="text-xs text-slate-500 mt-1">Must include https:// or http://</p>
            </div>

            <div>
              <Label htmlFor="domain">Domain</Label>
              <Input
                id="domain"
                value={formData.domain}
                onChange={(e) => handleInputChange('domain', e.target.value)}
                placeholder="example.com"
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Institutional">Institutional</SelectItem>
                  <SelectItem value="News">News</SelectItem>
                  <SelectItem value="FactCheck">FactCheck</SelectItem>
                  <SelectItem value="Science">Science</SelectItem>
                  <SelectItem value="Health">Health</SelectItem>
                  <SelectItem value="Politics">Politics</SelectItem>
                  <SelectItem value="Technology">Technology</SelectItem>
                  <SelectItem value="Economics">Economics</SelectItem>
                  <SelectItem value="Environment">Environment</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="credibility">Credibility</Label>
              <Select value={formData.credibility} onValueChange={(value) => handleInputChange('credibility', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Highest">Highest</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tier">Verification Tier</Label>
              <Select value={String(formData.tier)} onValueChange={(value) => handleInputChange('tier', parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Tier 1 - Primary/Institutional (Highest Trust)</SelectItem>
                  <SelectItem value="2">Tier 2 - Professional Media (High Trust)</SelectItem>
                  <SelectItem value="3">Tier 3 - State-Aligned/Ideological (Contextual)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Tier determines how AI weighs this source for verification</p>
            </div>

            <div>
              <Label htmlFor="region">Region (Optional)</Label>
              <Select value={formData.region || 'none'} onValueChange={(value) => handleInputChange('region', value === 'none' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="Global">Global</SelectItem>
                  <SelectItem value="US">US</SelectItem>
                  <SelectItem value="Europe">Europe</SelectItem>
                  <SelectItem value="Asia">Asia</SelectItem>
                  <SelectItem value="Middle East">Middle East</SelectItem>
                  <SelectItem value="Africa">Africa</SelectItem>
                  <SelectItem value="Latin America">Latin America</SelectItem>
                  <SelectItem value="Caribbean">Caribbean</SelectItem>
                  <SelectItem value="UK">UK</SelectItem>
                  <SelectItem value="China">China</SelectItem>
                  <SelectItem value="Russia">Russia</SelectItem>
                  <SelectItem value="India">India</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of the source"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={editingSource ? handleUpdateSource : handleAddSource}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {editingSource ? 'Update' : 'Add'} Source
              </Button>
              <Button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSourcesPage;
